import { BOARD_HEIGHT, BOARD_WIDTH, CELL, COLS, ROWS } from "../constants/board";
import { MAPS } from "../constants/maps";
import { TOWER_DEFS } from "../constants/towers";
import { canPlaceTower } from "../engine/engine";
import { drawEnemySprite, drawTowerSprite } from "./sprites";

const setCanvasScale = (canvas, ctx) => {
	const dpr = Math.min(window.devicePixelRatio || 1, 2);
	const width = BOARD_WIDTH * dpr;
	const height = BOARD_HEIGHT * dpr;

	if (canvas.width !== width || canvas.height !== height) {
		canvas.width = width;
		canvas.height = height;
	}

	ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
};

const glow = (ctx, color, blur, draw) => {
	ctx.save();
	ctx.shadowColor = color;
	ctx.shadowBlur = blur;
	draw();
	ctx.restore();
};

const drawGrid = (ctx, mapColor, time, options = {}) => {
	ctx.fillStyle = "#07080d";
	ctx.fillRect(0, 0, BOARD_WIDTH, BOARD_HEIGHT);

	const sweep = (time * 0.018) % CELL;
	ctx.strokeStyle = options.mobile ? "rgba(160,220,255,0.14)" : "rgba(180,210,255,0.07)";
	ctx.lineWidth = 1;
	for (let col = 0; col <= COLS; col++) {
		ctx.beginPath();
		ctx.moveTo(col * CELL, 0);
		ctx.lineTo(col * CELL, BOARD_HEIGHT);
		ctx.stroke();
	}
	for (let row = 0; row <= ROWS; row++) {
		ctx.beginPath();
		ctx.moveTo(0, row * CELL);
		ctx.lineTo(BOARD_WIDTH, row * CELL);
		ctx.stroke();
	}

	ctx.strokeStyle = mapColor;
	ctx.globalAlpha = options.mobile ? 0.14 : 0.09;
	ctx.beginPath();
	ctx.moveTo(0, sweep);
	for (let x = 0; x <= BOARD_WIDTH; x += 32) {
		ctx.lineTo(x, (sweep + Math.sin((x + time * 0.02) * 0.03) * 18) % BOARD_HEIGHT);
	}
	ctx.stroke();
	ctx.globalAlpha = 1;
};

const drawPath = (ctx, pathData, mapColor) => {
	const pathPx = pathData.px;

	glow(ctx, mapColor, 26, () => {
		ctx.strokeStyle = "rgba(255,255,255,0.13)";
		ctx.lineWidth = CELL * 0.72;
		ctx.lineCap = "round";
		ctx.lineJoin = "round";
		ctx.beginPath();
		ctx.moveTo(pathPx[0].x, pathPx[0].y);
		for (let i = 1; i < pathPx.length; i++) ctx.lineTo(pathPx[i].x, pathPx[i].y);
		ctx.stroke();
	});

	ctx.strokeStyle = mapColor;
	ctx.globalAlpha = 0.46;
	ctx.lineWidth = 2;
	ctx.setLineDash([14, 16]);
	ctx.beginPath();
	ctx.moveTo(pathPx[0].x, pathPx[0].y);
	for (let i = 1; i < pathPx.length; i++) ctx.lineTo(pathPx[i].x, pathPx[i].y);
	ctx.stroke();
	ctx.setLineDash([]);
	ctx.globalAlpha = 1;
};

const drawHover = (ctx, state, pathData) => {
	if (!state.selectedTower || !state.hoverCell || state.placementPreview?.active) return;

	const [col, row] = state.hoverCell;
	const placement = canPlaceTower(state, pathData, col, row);
	const x = col * CELL;
	const y = row * CELL;
	const def = TOWER_DEFS[state.selectedTower];

	ctx.fillStyle = placement.ok ? "rgba(124,247,212,0.16)" : "rgba(255,92,92,0.18)";
	ctx.fillRect(x + 3, y + 3, CELL - 6, CELL - 6);
	ctx.strokeStyle = placement.ok ? "#7cf7d4" : "#ff5c5c";
	ctx.lineWidth = 2;
	ctx.strokeRect(x + 4, y + 4, CELL - 8, CELL - 8);

	if (placement.ok) {
		ctx.beginPath();
		ctx.arc(x + CELL / 2, y + CELL / 2, def.range[0], 0, Math.PI * 2);
		ctx.strokeStyle = `${def.color}70`;
		ctx.lineWidth = 1;
		ctx.stroke();
	}
};

const drawPlacementPreview = (ctx, state, pathData) => {
	const preview = state.placementPreview;
	if (!preview?.active || !preview.type) return;

	const def = TOWER_DEFS[preview.type];
	const placement = canPlaceTower(state, pathData, preview.col, preview.row);
	const cellX = preview.col * CELL;
	const cellY = preview.row * CELL;
	const centerX = cellX + CELL / 2;
	const centerY = cellY + CELL / 2;
	const validColor = placement.ok ? "#7cf7d4" : "#ff5c5c";

	ctx.save();
	ctx.globalAlpha = 0.92;
	ctx.fillStyle = placement.ok ? "rgba(124,247,212,0.16)" : "rgba(255,92,92,0.2)";
	ctx.fillRect(cellX + 3, cellY + 3, CELL - 6, CELL - 6);
	ctx.strokeStyle = validColor;
	ctx.lineWidth = 2.5;
	ctx.strokeRect(cellX + 4, cellY + 4, CELL - 8, CELL - 8);

	ctx.beginPath();
	ctx.arc(preview.x, preview.y, def.range[0], 0, Math.PI * 2);
	ctx.strokeStyle = `${def.color}88`;
	ctx.lineWidth = 2;
	ctx.setLineDash([10, 9]);
	ctx.stroke();
	ctx.setLineDash([]);

	glow(ctx, def.color, 18, () => {
		ctx.beginPath();
		ctx.moveTo(preview.x, preview.y);
		ctx.lineTo(centerX, centerY);
		ctx.strokeStyle = `${def.color}55`;
		ctx.lineWidth = 1.5;
		ctx.stroke();
	});

	ctx.globalAlpha = placement.ok ? 0.9 : 0.58;
	drawTowerSprite(ctx, preview.type, preview.x, preview.y, 34, 0, true);
	ctx.globalAlpha = 1;

	ctx.fillStyle = "rgba(5,6,8,0.78)";
	ctx.strokeStyle = validColor;
	ctx.lineWidth = 1;
	ctx.beginPath();
	ctx.roundRect(preview.x + 16, preview.y - 34, 112, 28, 7);
	ctx.fill();
	ctx.stroke();
	ctx.fillStyle = placement.ok ? "#f8fbff" : "#ffd2d2";
	ctx.font = "800 11px Inter, system-ui, sans-serif";
	ctx.textAlign = "left";
	ctx.fillText(placement.ok ? "Release to place" : "Blocked cell", preview.x + 26, preview.y - 16);
	ctx.restore();
};

const drawTower = (ctx, tower, selected) => {
	const def = TOWER_DEFS[tower.type];
	const cx = tower.col * CELL + CELL / 2;
	const cy = tower.row * CELL + CELL / 2;
	const radius = 15 + (tower.pulse || 0) / 120;

	drawTowerSprite(ctx, tower.type, cx, cy, 30, tower.angle || 0, selected);
	ctx.strokeStyle = def.color;
	ctx.lineWidth = 3;
	ctx.beginPath();
	ctx.arc(cx, cy, radius, 0, Math.PI * 2);
	ctx.stroke();

	for (let i = 0; i <= tower.level; i++) {
		ctx.fillStyle = def.color;
		ctx.fillRect(tower.col * CELL + 10 + i * 8, tower.row * CELL + CELL - 10, 5, 4);
	}

	if (selected) {
		ctx.beginPath();
		ctx.arc(cx, cy, def.range[tower.level], 0, Math.PI * 2);
		ctx.strokeStyle = `${def.color}55`;
		ctx.lineWidth = 1;
		ctx.stroke();
	}
};

const drawEnemy = (ctx, enemy, now, options = {}) => {
	drawEnemySprite(ctx, enemy, now, options);

	if (enemy.heals) {
		ctx.strokeStyle = "rgba(157,242,127,0.65)";
		ctx.lineWidth = 2;
		ctx.beginPath();
		ctx.arc(enemy.x, enemy.y, enemy.size + 5, 0, Math.PI * 2);
		ctx.stroke();
	}
	if (enemy.reinforced) {
		ctx.strokeStyle = "rgba(220,230,240,0.82)";
		ctx.lineWidth = 3;
		ctx.beginPath();
		ctx.arc(enemy.x, enemy.y, enemy.size + 6, 0, Math.PI * 2);
		ctx.stroke();
	}
	if (enemy.fireman) {
		ctx.strokeStyle = "rgba(255,107,88,0.82)";
		ctx.lineWidth = 2;
		ctx.beginPath();
		ctx.arc(enemy.x, enemy.y, enemy.size + 4, 0, Math.PI * 2);
		ctx.stroke();
	}
	if (enemy.frostImmune) {
		ctx.strokeStyle = "rgba(115,216,255,0.9)";
		ctx.lineWidth = 2;
		ctx.beginPath();
		ctx.moveTo(enemy.x - 5, enemy.y - enemy.size - 6);
		ctx.lineTo(enemy.x + 5, enemy.y - enemy.size + 4);
		ctx.moveTo(enemy.x + 5, enemy.y - enemy.size - 6);
		ctx.lineTo(enemy.x - 5, enemy.y - enemy.size + 4);
		ctx.stroke();
	}

	if (now < enemy.slowEnd) {
		ctx.strokeStyle = "rgba(124,247,212,0.95)";
		ctx.lineWidth = 2;
		ctx.beginPath();
		ctx.arc(enemy.x, enemy.y, enemy.size + 8, 0, Math.PI * 2);
		ctx.stroke();
	}

	if (enemy.hp < enemy.maxHp) {
		const barW = enemy.size * 2.8;
		const barH = 4;
		const x = enemy.x - barW / 2;
		const y = enemy.y - enemy.size - 10;
		const ratio = Math.max(0, enemy.hp / enemy.maxHp);
		ctx.fillStyle = "rgba(0,0,0,0.6)";
		ctx.fillRect(x, y, barW, barH);
		ctx.fillStyle = ratio > 0.5 ? "#7cf27f" : ratio > 0.25 ? "#f8e45c" : "#ff5c5c";
		ctx.fillRect(x, y, barW * ratio, barH);
	}
};

const drawBeams = (ctx, beams, limit = beams.length) => {
	for (const beam of beams.slice(0, limit)) {
		const alpha = Math.max(0, beam.life / beam.maxLife);
		ctx.save();
		ctx.globalAlpha = alpha;
		ctx.strokeStyle = beam.color;
		ctx.lineWidth = beam.kind === "arc" ? 3 : 2;
		ctx.shadowColor = beam.color;
		ctx.shadowBlur = 16;
		if (beam.kind === "ring") {
			ctx.beginPath();
			ctx.arc(beam.x1, beam.y1, beam.radius * (1.05 - alpha * 0.15), 0, Math.PI * 2);
			ctx.stroke();
		} else {
			ctx.beginPath();
			ctx.moveTo(beam.x1, beam.y1);
			ctx.lineTo(beam.x2, beam.y2);
			ctx.stroke();
		}
		ctx.restore();
	}
};

const drawParticles = (ctx, state, options = {}) => {
	const projectileLimit = options.projectileLimit ?? state.projectiles.length;
	for (const projectile of state.projectiles.slice(0, projectileLimit)) {
		ctx.fillStyle = projectile.color;
		ctx.shadowColor = projectile.color;
		ctx.shadowBlur = 14;
		ctx.beginPath();
		ctx.arc(projectile.x, projectile.y, 3.5, 0, Math.PI * 2);
		ctx.fill();
		ctx.shadowBlur = 0;
	}

	const particleLimit = options.particleLimit ?? state.particles.length;
	for (const particle of state.particles.slice(0, particleLimit)) {
		const alpha = Math.max(0, particle.life / particle.maxLife);
		ctx.globalAlpha = alpha;
		ctx.fillStyle = particle.color;
		ctx.beginPath();
		ctx.arc(particle.x, particle.y, particle.size * alpha, 0, Math.PI * 2);
		ctx.fill();
	}
	ctx.globalAlpha = 1;

	const textLimit = options.textLimit ?? state.floatingTexts.length;
	for (const text of state.floatingTexts.slice(0, textLimit)) {
		const alpha = Math.max(0, text.life / text.maxLife);
		ctx.globalAlpha = alpha;
		ctx.fillStyle = text.color;
		ctx.font = "700 12px Inter, system-ui, sans-serif";
		ctx.textAlign = "center";
		ctx.fillText(text.text, text.x, text.y);
	}
	ctx.globalAlpha = 1;
};

export const renderGame = (canvas, state, pathData, options = {}) => {
	if (!canvas || !state || !pathData) return;

	const ctx = canvas.getContext("2d");
	const map = state.mapDef || MAPS[state.mapIdx] || MAPS[0];
	const now = performance.now();
	const viewport = options.viewport || { scale: 1, offsetX: 0, offsetY: 0 };
	const highDensity = state.enemies.length > 80 || options.performanceMode;
	const extremeDensity = state.enemies.length > 130;
	setCanvasScale(canvas, ctx);
	ctx.clearRect(0, 0, BOARD_WIDTH, BOARD_HEIGHT);

	ctx.fillStyle = "#050608";
	ctx.fillRect(0, 0, BOARD_WIDTH, BOARD_HEIGHT);

	ctx.save();
	ctx.translate(viewport.offsetX || 0, viewport.offsetY || 0);
	ctx.scale(viewport.scale || 1, viewport.scale || 1);

	drawGrid(ctx, map.color, now, { mobile: options.mobile });
	drawPath(ctx, pathData, map.color);
	drawHover(ctx, state, pathData);
	drawPlacementPreview(ctx, state, pathData);

	for (const tower of state.towers) drawTower(ctx, tower, state.selectedPlacedId === tower.id);
	for (const enemy of state.enemies) drawEnemy(ctx, enemy, now, { simplified: extremeDensity });
	drawBeams(ctx, state.beams, extremeDensity ? 18 : highDensity ? 32 : state.beams.length);
	drawParticles(ctx, state, {
		projectileLimit: extremeDensity ? 90 : highDensity ? 140 : state.projectiles.length,
		particleLimit: extremeDensity ? 80 : highDensity ? 140 : state.particles.length,
		textLimit: highDensity ? 18 : state.floatingTexts.length,
	});

	ctx.restore();

	if (options.paused) {
		ctx.fillStyle = "rgba(5,6,8,0.58)";
		ctx.fillRect(0, 0, BOARD_WIDTH, BOARD_HEIGHT);
		ctx.fillStyle = "#ffffff";
		ctx.font = "800 34px Inter, system-ui, sans-serif";
		ctx.textAlign = "center";
		ctx.fillText("PAUSED", BOARD_WIDTH / 2, BOARD_HEIGHT / 2);
	}
};
